import { supabase } from "@/integrations/supabase/client";

export interface MentorReview {
  id: string;
  session_id: string;
  player_id: string;
  mentor_id: string;
  rating: number;
  comment: string;
  is_public: boolean;
  created_at: string;
  player_profile?: {
    nickname: string;
    image_url?: string;
  };
  session?: {
    scheduled_date: string;
    mentorship_offerings?: {
      title: string;
    };
  };
}

/**
 * Busca todas as avaliações de um mentor
 */
export const getMentorReviews = async (mentorId: string): Promise<MentorReview[]> => {
  try {
    console.log('🔍 [getMentorReviews] Buscando avaliações para mentor ID:', mentorId);
    
    // Buscar avaliações usando mentor_id (que referencia profiles.id diretamente)
    const { data: reviews, error: reviewsError } = await supabase
      .from('session_reviews')
      .select('*')
      .eq('mentor_id', mentorId)
      .order('created_at', { ascending: false });

    console.log('🔍 [getMentorReviews] Avaliações encontradas:', { count: reviews?.length || 0, error: reviewsError });

    if (reviewsError) {
      console.error('Error fetching mentor reviews:', reviewsError);
      throw reviewsError;
    }

    if (!reviews || reviews.length === 0) {
      return [];
    }

    // Buscar perfis dos jogadores
    const playerIds = [...new Set(reviews.map(r => r.player_id))];
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, nickname, image_url')
      .in('id', playerIds);

    if (profilesError) {
      console.error('Error fetching player profiles:', profilesError);
    }

    // Buscar dados das sessões
    const sessionIds = [...new Set(reviews.map(r => r.session_id))];
    const { data: sessions, error: sessionsError } = await supabase
      .from('sessions')
      .select(`
        id,
        scheduled_date,
        mentorship_offering_id,
        mentorship_offerings (
          title
        )
      `)
      .in('id', sessionIds);

    if (sessionsError) {
      console.error('Error fetching sessions:', sessionsError);
    }

    // Combinar os dados
    const enrichedReviews = reviews.map(review => {
      const playerProfile = profiles?.find(p => p.id === review.player_id);
      const session = sessions?.find(s => s.id === review.session_id);

      return {
        ...review,
        player_profile: playerProfile ? {
          nickname: playerProfile.nickname,
          image_url: playerProfile.image_url
        } : undefined,
        session: session ? {
          scheduled_date: session.scheduled_date,
          mentorship_offerings: session.mentorship_offerings
        } : undefined
      };
    });

    return enrichedReviews as unknown as MentorReview[];
  } catch (error) {
    console.error('Error in getMentorReviews:', error);
    throw error;
  }
};

/**
 * Busca apenas as avaliações públicas de um mentor
 */
export const getPublicMentorReviews = async (mentorId: string): Promise<MentorReview[]> => {
  try {
    console.log('🔍 [getPublicMentorReviews] Buscando avaliações públicas para mentor ID:', mentorId);
    
    // Buscar avaliações públicas usando mentor_id
    const { data: reviews, error: reviewsError } = await supabase
      .from('session_reviews')
      .select('*')
      .eq('mentor_id', mentorId)
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .limit(10);

    console.log('🔍 [getPublicMentorReviews] Avaliações públicas encontradas:', { count: reviews?.length || 0, error: reviewsError });

    if (reviewsError) {
      console.error('Error fetching public mentor reviews:', reviewsError);
      throw reviewsError;
    }

    if (!reviews || reviews.length === 0) {
      return [];
    }

    // Buscar perfis dos jogadores
    const playerIds = [...new Set(reviews.map(r => r.player_id))];
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, nickname, image_url')
      .in('id', playerIds);

    if (profilesError) {
      console.error('Error fetching player profiles:', profilesError);
    }

    // Buscar dados das sessões
    const sessionIds = [...new Set(reviews.map(r => r.session_id))];
    const { data: sessions, error: sessionsError } = await supabase
      .from('sessions')
      .select(`
        id,
        scheduled_date,
        mentorship_offering_id,
        mentorship_offerings (
          title
        )
      `)
      .in('id', sessionIds);

    if (sessionsError) {
      console.error('Error fetching sessions:', sessionsError);
    }

    // Combinar os dados
    const enrichedReviews = reviews.map(review => {
      const playerProfile = profiles?.find(p => p.id === review.player_id);
      const session = sessions?.find(s => s.id === review.session_id);

      return {
        ...review,
        player_profile: playerProfile ? {
          nickname: playerProfile.nickname,
          image_url: playerProfile.image_url
        } : undefined,
        session: session ? {
          scheduled_date: session.scheduled_date,
          mentorship_offerings: session.mentorship_offerings
        } : undefined
      };
    });

    return enrichedReviews as unknown as MentorReview[];
  } catch (error) {
    console.error('Error in getPublicMentorReviews:', error);
    throw error;
  }
};

/**
 * Atualiza a visibilidade pública de uma avaliação
 */
export const updateReviewVisibility = async (reviewId: string, isPublic: boolean): Promise<void> => {
  try {
    const { error } = await supabase
      .from('session_reviews')
      .update({ is_public: isPublic })
      .eq('id', reviewId);

    if (error) {
      console.error('Error updating review visibility:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in updateReviewVisibility:', error);
    throw error;
  }
};

/**
 * Busca estatísticas de avaliações do mentor
 */
export const getMentorRatingStats = async (mentorId: string) => {
  try {
    console.log('📊 [getMentorRatingStats] Buscando estatísticas para mentor ID (profile_id):', mentorId);
    
    // Buscar TODAS as avaliações (públicas e privadas) para calcular a média
    // A média deve considerar todas as avaliações, não apenas as públicas
    const { data: reviews, error } = await supabase
      .from('session_reviews')
      .select('rating')
      .eq('mentor_id', mentorId);

    console.log('📊 [getMentorRatingStats] Reviews encontradas (todas):', { count: reviews?.length || 0, error });

    if (error) {
      console.error('Error fetching mentor rating stats:', error);
      throw error;
    }

    if (!reviews || reviews.length === 0) {
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      };
    }

    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = parseFloat((totalRating / reviews.length).toFixed(1));

    // Distribuição das notas
    const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach(review => {
      ratingDistribution[review.rating as keyof typeof ratingDistribution]++;
    });

    console.log('📊 [getMentorRatingStats] Estatísticas calculadas:', {
      averageRating,
      totalReviews: reviews.length,
      ratingDistribution
    });

    return {
      averageRating,
      totalReviews: reviews.length,
      ratingDistribution
    };
  } catch (error) {
    console.error('Error in getMentorRatingStats:', error);
    throw error;
  }
}; 